/**
 * WrapUp.tsx — the call-ended state (GUI v2 req 8.4 + A.5: score becomes
 * primary). Renders the 4-item result, the seven-category PROVISIONAL
 * scorecard (18_call-scorecard.md — authoritative grade still comes from the
 * after-call AI pipeline), auto-fail warnings, the captured call-card data
 * with one-tap copy, the quote summary, the completeness sweep, and the
 * 16-disposition picker.
 */
import { useMemo, useState } from "react";
import type { BehaviorKey } from "@/types";
import { AUTO_FAILS, computeProvisionalScorecard } from "@/live/content/scorecard";
import { DISPOSITIONS } from "@/live/content/dispositions";
import { COMPLETENESS_SWEEP } from "@/live/content/close";
import { quoteSummary, recommendTier } from "@/live/content/pricing";
import { REASON_LABELS } from "@/live/CallCard";
import { PanelLabel } from "@/live/panels";
import type { LiveV2State } from "@/live/state";

export function WrapUp({
  state,
  onDisposition,
}: {
  state: LiveV2State;
  onDisposition: (key: string) => void;
}) {
  const [copied, setCopied] = useState<string | null>(null);
  const behaviors = useMemo(() => new Set<BehaviorKey>(state.behaviors), [state.behaviors]);
  const scorecard = useMemo(
    () =>
      computeProvisionalScorecard(behaviors, {
        objectionRaised: state.detectedObjections.length > 0,
        priceStated: state.priceStated,
      }),
    [behaviors, state.detectedObjections.length, state.priceStated],
  );
  const checklistTotal = state.checklist.reduce((sum, c) => sum + (c.covered ? c.weight : 0), 0);
  const card = state.card;
  const vehicle = card.vehicles[card.activeVehicle] ?? card.vehicles[0];

  // Auto-fail evaluation at wrap-up (18 §5; conditions 1+2 are end-of-call).
  const minimumsMissing =
    card.firstName.value === null ||
    card.phone.value === null ||
    !vehicle?.model.value ||
    card.serviceInterest.value === null;
  const fails: string[] = [];
  if (state.priceStated && minimumsMissing) fails.push("no_crm_minimums");
  const closeAttempted = state.behaviors.some((b) =>
    ["booking_asked", "deposit_asked", "showroom_invited", "followup_set", "booking_confirmed"].includes(b),
  );
  if (!closeAttempted) fails.push("no_close_attempt");
  for (const hit of state.complianceHistory) {
    if (["fuel_numbers", "flat_heat_number", "off_sheet_spec", "scratch_proof", "coating_as_chip_protection", "financing_guarantee"].includes(hit.alertId)) {
      if (!fails.includes("prohibited_claim")) fails.push("prohibited_claim");
    }
    if (hit.alertId === "legal_improvised" && !fails.includes("improvised_legal")) fails.push("improvised_legal");
  }

  const summary = vehicle && vehicle.pricingClass
    ? quoteSummary(vehicle, recommendTier(card.reasons.map((r) => r.key)).anchor)
    : null;

  const copyAll = () => {
    const lines = [
      `Customer: ${[card.firstName.value, card.lastName.value].filter(Boolean).join(" ") || "(not captured)"}`,
      `Phone: ${card.phone.value ?? "(not captured)"}`,
      `Vehicle: ${vehicle ? [vehicle.year.value, vehicle.make.value, vehicle.model.value].filter(Boolean).join(" ") || "(not captured)" : "(not captured)"}`,
      `Service interest: ${card.serviceInterest.value ?? "(not captured)"}`,
      `Reasons: ${card.reasons.map((r) => REASON_LABELS[r.key]).join(", ") || "(none)"}`,
      summary ? `Quote: ${summary}` : null,
      `Disposition: ${DISPOSITIONS.find((d) => d.key === state.disposition)?.label ?? "(not set)"}`,
      `Live checklist: ${checklistTotal}/100 · Provisional scorecard: ${scorecard.total}/100`,
    ].filter(Boolean);
    void navigator.clipboard?.writeText(lines.join("\n"));
    setCopied("all");
    window.setTimeout(() => setCopied(null), 1500);
  };

  return (
    <div className="space-y-3">
      {/* Score primary at wrap-up (req A.5 / 2.4) */}
      <section className="rounded-lg border bg-card p-4 text-center">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Call score (live checklist)</p>
        <p className="mt-1 text-4xl font-bold tabular-nums">{checklistTotal}<span className="text-lg text-muted-foreground">/100</span></p>
        <div className="mt-2 flex flex-wrap justify-center gap-1.5">
          {state.checklist.map((c) => (
            <span
              key={c.key}
              className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${c.covered ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300" : "bg-muted text-muted-foreground"}`}
            >
              {c.covered ? "✓" : "✕"} {c.label}
            </span>
          ))}
        </div>
      </section>

      {fails.length > 0 && (
        <section className="rounded-lg border-2 border-red-500 bg-red-50 p-3 dark:bg-red-950/40">
          <p className="text-sm font-bold text-red-700 dark:text-red-300">Automatic fail condition{fails.length > 1 ? "s" : ""} (scorecard §5)</p>
          {fails.map((f) => (
            <p key={f} className="mt-1 text-xs text-red-800/90 dark:text-red-200/90">
              <span className="font-semibold">{AUTO_FAILS[f]?.label}:</span> {AUTO_FAILS[f]?.detail}
            </p>
          ))}
        </section>
      )}

      {/* Seven-category provisional scorecard (req G.5) */}
      <section className="space-y-1.5">
        <div className="flex items-center justify-between">
          <PanelLabel>Scorecard (provisional)</PanelLabel>
          <span className="text-xs tabular-nums text-muted-foreground">{scorecard.total}/100</span>
        </div>
        <ul className="rounded-lg border bg-card">
          {scorecard.categories.map((c) => (
            <li key={c.category.key} className="flex items-center justify-between border-b px-3 py-1.5 text-sm last:border-0">
              <span className="min-w-0 truncate">{c.category.label} <span className="text-[11px] text-muted-foreground">({c.category.weight}%)</span></span>
              <span className={`text-xs font-medium ${c.notObserved ? "text-muted-foreground" : c.points >= 80 ? "text-emerald-600 dark:text-emerald-400" : c.points >= 50 ? "text-amber-600 dark:text-amber-400" : "text-red-600 dark:text-red-400"}`}>
                {c.label}
              </span>
            </li>
          ))}
        </ul>
        <p className="text-[11px] text-muted-foreground">
          Provisional, from live detections — the authoritative score comes from the after-call AI scorecard.
        </p>
      </section>

      {/* Completeness sweep (16 §10) */}
      <section className="space-y-1.5">
        <PanelLabel>Completeness check</PanelLabel>
        <div className="flex flex-wrap gap-1.5">
          <SweepChip label="Name" ok={card.firstName.value !== null && card.lastName.value !== null} />
          <SweepChip label="Phone" ok={card.phone.value !== null} />
          <SweepChip label="Vehicle" ok={!!vehicle?.model.value} />
          <SweepChip label="Service" ok={card.serviceInterest.value !== null} />
          <SweepChip label="Reason" ok={card.reasons.length > 0} />
        </div>
        <p className="text-[11px] text-muted-foreground">“{COMPLETENESS_SWEEP.line}”</p>
      </section>

      {/* Outcome disposition (27 — most common first; proposed order) */}
      <section className="space-y-1.5">
        <PanelLabel>Outcome</PanelLabel>
        <div className="flex flex-wrap gap-1.5">
          {DISPOSITIONS.map((d) => (
            <button
              key={d.key}
              type="button"
              onClick={() => onDisposition(d.key)}
              title={d.definition}
              className={`min-h-[34px] rounded-full border px-3 text-xs font-medium ${state.disposition === d.key ? "border-primary bg-primary text-primary-foreground" : "hover:bg-accent"}`}
            >
              {d.label}
            </button>
          ))}
        </div>
      </section>

      {summary && (
        <section className="rounded-lg border bg-card p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Quote summary</p>
          <p className="mt-1 text-sm">“{summary}”</p>
        </section>
      )}

      <button
        type="button"
        onClick={copyAll}
        className="w-full rounded-md bg-primary px-3 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90"
      >
        {copied ? "Copied ✓" : "Copy captured details for booking"}
      </button>
    </div>
  );
}

function SweepChip({ label, ok }: { label: string; ok: boolean }) {
  return (
    <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${ok ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300" : "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300"}`}>
      {ok ? "✓" : "✕"} {label}
    </span>
  );
}
